/**
 *
 * FamilyFreightSimulateForm
 *
 */

import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormik } from 'formik';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import React from 'react';

// Containers
import makeSelectUserProvider from 'containers/common/UserProvider/selectors';

// Components
import { Grid, Mui, Paper, TextField, Typography } from 'components';
import { DisplayData, InputMask } from 'components/common';

// Local Components
import * as actions from './actions';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';
import makeSelectFamilyFreightSimulateForm from './selectors';

export function FamilyFreightSimulateForm(props) {
  useInjectReducer({ key: 'familyFreightSimulateForm', reducer });
  useInjectSaga({ key: 'familyFreightSimulateForm', saga });

  const {
    familyFreightSimulateForm,
    listRequest,
    productSlug,
    enrollmentSlug,
    userProvider: {
      user: { addresses = [] },
    },
  } = props;

  const { postCode, isFetching } = familyFreightSimulateForm;

  const intl = useIntl();

  const list = familyFreightSimulateForm.list[productSlug];

  const formMessages = {
    postCode: {
      label: intl.formatMessage(messages.inputLabel),
      required: '',
      helperText: '',
      invalid: '',
    },
  };

  const initialValues = {
    postCode: postCode || (addresses && addresses.length > 0 && addresses[0].postcode) || '',
  };

  const yup = Yup.object().shape({
    postCode: Yup.string().required(intl.formatMessage(messages.postCodeRequired)),
  });

  const onSubmit = (values, { resetForm }) => {
    if (productSlug) listRequest(enrollmentSlug, productSlug, values.postCode);

    resetForm({ values: initialValues });
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema: yup,
    enableReinitialize: true,
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          name="postCode"
          margin="none"
          formik={formik}
          messages={formMessages}
          onKeyPress={event => {
            if (event.key === 'Enter') {
              formik.handleSubmit();
              event.preventDefault();
            }
          }}
          InputProps={{
            inputComponent: InputMask.MaskedPostalCode,
            endAdornment: (
              <Mui.InputAdornment position="end">
                <Mui.IconButton
                  disabled={isFetching.list[productSlug]}
                  color="primary"
                  onClick={() => {
                    formik.handleSubmit();
                  }}>
                  <Mui.Icon>local_shipping</Mui.Icon>
                </Mui.IconButton>
              </Mui.InputAdornment>
            ),
          }}
        />
      </Grid>
      {isFetching.list[productSlug] && (
        <Grid item xs={12}>
          <center>
            <Mui.CircularProgress size={24} />
          </center>
        </Grid>
      )}
      {list &&
        list.map(option => (
          <Grid item xs={12} key={option.carrierUid}>
            <Paper variant="outlined">
              <Mui.Box p={2}>
                <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                  <Grid item xs>
                    <Typography variant="body1">{option.carrier}</Typography>
                    <Typography variant="body2" color="primary" component="span">
                      <strong>{option.timeCost}</strong>
                    </Typography>{' '}
                    <Typography variant="body2" component="span">
                      <FormattedMessage
                        {...messages.estimatedDeliveryDateLabel}
                        values={{
                          deliveryDate: (
                            <strong>
                              <DisplayData.DateField type="date" value={option.deliveryDate} />
                            </strong>
                          ),
                        }}
                      />
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="h6" color="primary">
                      <DisplayData.CurrencyField free value={option.cost} />
                    </Typography>
                  </Grid>
                </Grid>
              </Mui.Box>
            </Paper>
          </Grid>
        ))}
      {list && list.length === 0 && (
        <Grid item xs={12}>
          <Typography variant="body1">
            <FormattedMessage
              {...messages.noOptionsMessage}
              values={{ postCode: <strong>{postCode}</strong> }}
            />
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}

FamilyFreightSimulateForm.propTypes = {
  familyFreightSimulateForm: PropTypes.object.isRequired,
  listRequest: PropTypes.func.isRequired,
  productSlug: PropTypes.string,
  userProvider: PropTypes.object.isRequired,
  enrollmentSlug: PropTypes.any,
};

const mapStateToProps = createStructuredSelector({
  familyFreightSimulateForm: makeSelectFamilyFreightSimulateForm(),
  userProvider: makeSelectUserProvider(),
});

function mapDispatchToProps(dispatch) {
  return {
    listRequest: (enrollmentSlug, productSlug, postCode) =>
      dispatch(actions.listRequest({ enrollmentSlug, productSlug, postCode })),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(FamilyFreightSimulateForm);
